import { useState } from 'react';
import { Button } from '../../../../../components/ui/button';
import { ReasonButton } from '../../../../../components/ui/ReasonButton';
import { Input } from '../../../../../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { createStorageTable, newStorageRequestId } from '../../services/azureStorageApi';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { storageTableNameProblem } from '../tree/tableName';
import { tableAdminFailureText } from './tableAdminFailure';
import { getConnectionById } from '../../../../connections/utils/connectionLookup';
import { recordTableAdminOutcome } from '../model/tableAdminRefusals';
import { StorageAccountName } from '../../components/StorageAccountName';

type Props = {
  open: boolean;
  connectionId: string;
  onOpenChange: (open: boolean) => void;
  disabledReason?: string;
  onCreate?: (table: string) => Promise<{ table: string }>;
  onCreated?: (table: string) => void;
};

export function CreateStorageTableDialog({ open, connectionId, onOpenChange, disabledReason, onCreate, onCreated }: Props) {
  const [table, setTable] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const problem = storageTableNameProblem(table);

  const submit = async (): Promise<void> => {
    // Enter submits (round B A15): a second Enter while the create is in flight is a no-op, never a second request.
    if (saving) return;
    if (problem || disabledReason) { setError(problem ?? disabledReason); return; }
    setSaving(true);
    setError(undefined);
    trackStorageEvent('feature:storage_table_admin', { action: 'create', outcome: 'attempt' });
    try {
      const result = await (onCreate?.(table) ?? createStorageTable({ connectionId, table, requestId: newStorageRequestId(), timeoutMs: 30_000 }));
      trackStorageEvent('feature:storage_table_admin', { action: 'create', outcome: 'success' });
      const connection = getConnectionById(connectionId).connection;
      if (connection) recordTableAdminOutcome(connection, { op: 'create' });
      onCreated?.(result.table);
      onOpenChange(false);
      setTable('');
    } catch (failure) {
      trackStorageEvent('feature:storage_table_admin', { action: 'create', outcome: 'failure' });
      // C2 R10: a permission refusal (main names tables/write, or the SAS letters) is remembered, so the menus say why before the next try.
      const connection = getConnectionById(connectionId).connection;
      if (connection) recordTableAdminOutcome(connection, { op: 'create' }, failure);
      setError(tableAdminFailureText('created', failure));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-storage-table-dialog">
        <form className="grid gap-4" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          <DialogHeader><DialogTitle>Create table</DialogTitle><DialogDescription>Create an Azure Table storage table in storage account <StorageAccountName connectionId={connectionId} />.</DialogDescription></DialogHeader>
          <label className="grid gap-2 text-sm"><span>Table name</span><Input aria-label="Table name" spellCheck={false} value={table} onChange={(event) => { setTable(event.target.value); setError(undefined); }} autoFocus /></label>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {/* A9: a persistent refusal stays focusable with its reason; `saving` is a transient native disabled. */}
            <ReasonButton type="submit" reason={disabledReason} disabled={saving}>Create table</ReasonButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
